import { flushSync } from 'react-dom';
import { useState, useCallback, forwardRef, useEffect, createContext, useRef, useContext, useMemo, createElement, useId as useId$1, Fragment as Fragment$1 } from 'react';
import { useLayoutEffect, Box, usePreventScroll, mergeProps as mergeProps$1, useObjectRef, unsafe_keyframes, useZIndexContext, useTokens, useFocusable, useHoverable, usePressable, useRenderLink, useResizeObserver, useTranslation, Overlay, getRootFontSizeValue, FocusableProvider } from '@gemini/core';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { mergeProps, useId, useViewportSize } from '@react-aria/utils';
import { useOverlay, useOverlayFocusContain, ariaHideOutside, useOverlayTrigger, useOverlayPosition } from '@react-aria/overlays';
import { LoaderIcon, CloseIcon, InfoCircleIcon, InfoFilledCircleIcon, ExclamationFilledCircleIcon, CheckFilledCircleIcon, CloseFilledCircleIcon, CheckFilledIcon, FilterSlidersIcon } from '@gemini/icons';
import { useTooltipTrigger as useTooltipTrigger$1, useTooltip as useTooltip$1 } from '@react-aria/tooltip';
import { useTooltipTriggerState } from '@react-stately/tooltip';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useCheckbox as useCheckbox$1, useCheckboxGroupItem, useCheckboxGroup as useCheckboxGroup$1 } from '@react-aria/checkbox';
import { useToggleState } from '@react-stately/toggle';
import { useCheckboxGroupState } from '@react-stately/checkbox';
import { useField as useField$1 } from '@react-aria/label';
import { GregorianCalendar } from '@internationalized/date';
import { CartesianGrid, XAxis, YAxis, Tooltip as Tooltip$1 } from 'recharts';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { useDialog } from '@react-aria/dialog';

function useEnterAnimation(ref, isReady = true) {
    const [isEntering, setEntering] = useState(true);
    const isAnimationReady = isEntering && isReady;
    // There are two cases for entry animations:
    // 1. CSS @keyframes. The `animation` property is set during the isEntering state, and it is removed after the animation finishes.
    // 2. CSS transitions. The initial styles are applied during the isEntering state, and removed immediately, causing the transition to occur.
    //
    // In the second case, cancel any transitions that were triggered prior to the isEntering = false state (when the transition is supposed to start).
    // This can happen when isReady starts as false (e.g. popovers prior to placement calculation).
    useLayoutEffect(()=>{
        if (isAnimationReady && ref.current && 'getAnimations' in ref.current) {
            for (const animation of ref.current.getAnimations()){
                if (animation instanceof CSSTransition) {
                    animation.cancel();
                }
            }
        }
    }, [
        ref,
        isAnimationReady
    ]);
    useAnimation(ref, isAnimationReady, useCallback(()=>setEntering(false), []));
    return isAnimationReady;
}
function useExitAnimation(ref, isOpen) {
    const [exitState, setExitState] = useState(isOpen ? 'open' : 'closed');
    switch(exitState){
        case 'open':
            // If isOpen becomes false, set the state to exiting.
            if (!isOpen) {
                setExitState('exiting');
            }
            break;
        case 'closed':
        case 'exiting':
            // If we are exiting and isOpen becomes true, the animation was interrupted.
            // Reset the state to open.
            if (isOpen) {
                setExitState('open');
            }
            break;
    }
    const isExiting = exitState === 'exiting';
    useAnimation(ref, isExiting, useCallback(()=>{
        // Set the state to closed, which will cause the element to be unmounted.
        setExitState((state)=>state === 'exiting' ? 'closed' : state);
    }, []));
    return isExiting;
}
function useAnimation(ref, isActive, onEnd) {
    useLayoutEffect(()=>{
        if (!isActive || !ref.current) return;
        if (!('getAnimations' in ref.current)) {
            // JSDOM
            onEnd();
            return;
        }
        const animations = ref.current.getAnimations();
        if (animations.length === 0) {
            onEnd();
            return;
        }
        let canceled = false;
        Promise.all(animations.map((a)=>a.finished)).then(()=>{
            if (!canceled) {
                flushSync(()=>{
                    onEnd();
                });
            }
        }).catch(()=>{});
        return ()=>{
            canceled = true;
        };
    }, [
        ref,
        isActive,
        onEnd
    ]);
}

const Backdrop = /*#__PURE__*/ forwardRef((props, ref)=>{
    return /*#__PURE__*/ jsx(Box, {
        ...props,
        ref: ref,
        as: "div",
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: "unsafe_component.backdrop.color.bg",
        "aria-hidden": "true"
    });
});

/**
 * Provides the behavior and accessibility implementation for a modal component.
 * A modal is an overlay element which blocks interaction with elements outside it.
 */ function useModalOverlay(props, state, ref) {
    const { overlayProps, underlayProps } = useOverlay({
        ...props,
        isOpen: state.isOpen,
        onClose: state.close
    }, ref);
    usePreventScroll({
        isDisabled: !state.isOpen
    });
    useOverlayFocusContain();
    useEffect(()=>{
        if (state.isOpen && ref.current) {
            return ariaHideOutside([
                ref.current
            ]);
        }
        return ()=>{};
    }, [
        state.isOpen,
        ref
    ]);
    return {
        modalProps: mergeProps(overlayProps),
        underlayProps
    };
}

/**
 * Custom hook to map `ModalDialog` props to `Box` ones with all needed interactive and accessible HTML attributes.
 * @returns dialog and title props, close action
 */ const useModalDialog = (ref, props)=>{
    const { state } = useModalContext();
    const { dialogProps, titleProps } = useDialog({
        id: props.id
    }, ref);
    return {
        boxProps: {
            dialog: mergeProps$1(dialogProps),
            title: titleProps
        },
        close: state?.close
    };
};
/**
 * Custom hook to map `ModalTrigger` props to `Box` ones with all needed interactive and accessible HTML attributes.
 * @param props Modal props
 * @returns trigger and overlay box props, and overlay state
 */ const useModal = (props)=>{
    const underlayRef = useRef(null);
    const triggerRef = useRef(null);
    const modalWrapperRef = useRef(null);
    const state = useOverlayTriggerState(props);
    const { triggerProps, overlayProps } = useOverlayTrigger({
        type: 'dialog'
    }, state);
    const fallbackId = useId();
    // Exit animation should be handle here as the showing condition is done in the return here
    const isOverlayExiting = useExitAnimation(underlayRef, state.isOpen);
    const isModalExiting = useExitAnimation(modalWrapperRef, state.isOpen);
    const isExiting = isOverlayExiting || isModalExiting;
    return {
        boxProps: {
            trigger: mergeProps$1(triggerProps, {
                ref: triggerRef,
                'aria-haspopup': 'dialog',
                'aria-controls': overlayProps.id ? triggerProps['aria-controls'] : fallbackId
            }),
            overlay: mergeProps$1(overlayProps, {
                id: overlayProps.id ?? fallbackId
            })
        },
        refs: {
            underlay: underlayRef,
            wrapper: modalWrapperRef
        },
        isExiting,
        state
    };
};
/**
 * Custom hook to map `ModalWrapper` props to `Box` ones with all needed interactive and accessible HTML attributes.
 * @param ref the ref of the modal wrapper
 * @param props ModalWrapper props
 * @returns modal and underlay box props
 */ const useModalWrapper = (ref, props)=>{
    const modalObjectRef = useObjectRef(ref);
    const { state } = useModalContext();
    const { modalProps, underlayProps } = useModalOverlay(props, // Wrapper will be in dom when triggered, so state will be there as that time and not undefined
    state, modalObjectRef);
    const entering = useEnterAnimation(props.underlayRef);
    return {
        boxProps: {
            modal: mergeProps$1(modalProps, {
                ref: modalObjectRef
            }),
            underlay: underlayProps
        },
        entering
    };
};
const ModalContext = createContext({});
function useModalContext() {
    const contextModal = useContext(ModalContext);
    if (!contextModal) {
        throw new Error('Cannot call useModalContext outside a <ModalTrigger>.');
    }
    return contextModal;
}

const fade = unsafe_keyframes({
    from: {
        opacity: 0
    },
    to: {
        opacity: 1
    }
});
const fadeAndScale = unsafe_keyframes({
    from: {
        opacity: 0,
        transform: 'translate(-50%,50%) scale(0.96)'
    },
    to: {
        opacity: 1,
        transform: 'translate(-50%,50%) scale(1)'
    }
});
const slide = unsafe_keyframes({
    from: {
        transform: 'translateY(100%)'
    },
    to: {
        transform: 'translateY(0%)'
    }
});
const ModalWrapper = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { children, isDismissable, isKeyboardDismissDisabled, displayMode, predefinedWidth, underlayRef, isExiting, ...restProps } = props;
    const { boxProps, entering } = useModalWrapper(ref, props);
    const zIndex = useZIndexContext();
    const viewportSize = useViewportSize();
    return /*#__PURE__*/ jsxs(Box, {
        position: "fixed",
        // Related to iOS issues with keyboard position over modal
        // Setting bottom={0} is making modal going under ios keyboard while height={viewportSize.height} is taking the keyboard into account
        // This is using the `visualViewPort` API to get the correct viewport size (vh/dvh units doesn't seem to work correctly with that case)
        // https://github.com/adobe/react-spectrum/issues/5926#issuecomment-2257306911
        height: viewportSize.height,
        maxHeight: "100vh",
        top: 0,
        left: 0,
        right: 0,
        overflow: [
            'hidden',
            'clip'
        ],
        unsafe_style: {
            zIndex
        },
        children: [
            /*#__PURE__*/ jsx(Backdrop, {
                ...boxProps.underlay,
                ref: underlayRef,
                animationDuration: isExiting ? 'unsafe_component.modal.motionDuration.exiting' : 'unsafe_component.modal.motionDuration.entering',
                animationTimingFunction: isExiting ? 'unsafe_component.modal.motionEasing.exiting' : 'unsafe_component.modal.motionEasing.entering',
                animationName: entering || isExiting ? fade : undefined,
                animationDirection: isExiting ? 'reverse' : undefined
            }),
            /*#__PURE__*/ jsx(Sheet, {
                isExiting: isExiting,
                entering: entering,
                displayMode: displayMode,
                predefinedWidth: predefinedWidth,
                ...mergeProps$1(boxProps.modal, restProps),
                children: children
            })
        ]
    });
});
const Sheet = /*#__PURE__*/ forwardRef(({ children, displayMode, predefinedWidth, isExiting, entering, ...restProps }, ref)=>{
    const tokens = useTokens();
    const position = {
        'breakpoint.base': 0,
        'breakpoint.md': displayMode !== 'fullscreen' ? '50%' : 0
    };
    return /*#__PURE__*/ jsx(Box, {
        ...restProps,
        ref: ref,
        as: "div",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "unsafe_component.modal.color.bg",
        boxShadow: `unsafe_component.modal.shadow.${displayMode}`,
        borderRadius: `unsafe_component.modal.radius.${displayMode}`,
        borderBottomLeftRadius: {
            'breakpoint.base': 'radius.none',
            'breakpoint.md': `unsafe_component.modal.radius.${displayMode}`
        },
        borderBottomRightRadius: {
            'breakpoint.base': 'radius.none',
            'breakpoint.md': `unsafe_component.modal.radius.${displayMode}`
        },
        borderColor: "unsafe_component.modal.color.border",
        borderStyle: "solid",
        borderWidth: `unsafe_component.modal.borderWidth.${displayMode}`,
        position: "absolute",
        left: position,
        bottom: position,
        transform: {
            'breakpoint.base': 'none',
            'breakpoint.md': displayMode !== 'fullscreen' ? 'translate(-50%,50%)' : 'none'
        },
        height: `unsafe_component.modal.sizing.height.${displayMode}`,
        maxHeight: displayMode !== 'fullscreen' ? {
            'breakpoint.base': `calc(100% - ${tokens['unsafe_component.modal.spacing.gap.mobileTop']})`,
            'breakpoint.md': '80%'
        } : '100%',
        overflow: [
            'hidden',
            'clip'
        ],
        width: {
            'breakpoint.base': '100%',
            // When predefined width 1120px is bigger than viewport, the modal width is 80%, as maxHeight, to avoid left and right hidden parts
            'breakpoint.md': displayMode !== 'fullscreen' ? predefinedWidth !== '1120' ? `unsafe_component.modal.sizing.predefinedWidth.${predefinedWidth}` : '80%' : undefined,
            'breakpoint.xl': displayMode !== 'fullscreen' ? `unsafe_component.modal.sizing.predefinedWidth.${predefinedWidth}` : undefined
        },
        animationDuration: isExiting ? 'unsafe_component.modal.motionDuration.exiting' : 'unsafe_component.modal.motionDuration.entering',
        animationTimingFunction: isExiting ? 'unsafe_component.modal.motionEasing.exiting' : 'unsafe_component.modal.motionEasing.entering',
        animationDirection: isExiting ? 'reverse' : undefined,
        animationName: entering ? {
            'breakpoint.base': slide,
            'breakpoint.md': displayMode !== 'fullscreen' ? fadeAndScale : fade
        } : isExiting ? {
            'breakpoint.base': slide,
            'breakpoint.md': fade
        } : undefined,
        children: children
    });
});

const Badge = ({ variant = 'primary', size = '24', isDisabled, label, children, ...restProps })=>{
    const tokens = useTokens();
    const sizeBoxProps = useMemo(()=>mapSizeToBoxProps(size), [
        size
    ]);
    const variantBoxProps = useMemo(()=>mapVariantToBoxProps(variant, !!isDisabled), [
        variant,
        isDisabled
    ]);
    return /*#__PURE__*/ jsx(Box, {
        ...restProps,
        as: "span",
        role: "status",
        "aria-label": label,
        "aria-disabled": isDisabled,
        cursor: isDisabled ? 'not-allowed' : undefined,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: `calc(${tokens['unsafe_component.badge.spacing.padding.horizontal']} - ${tokens['unsafe_component.badge.borderWidth']})`,
        borderStyle: "solid",
        borderWidth: "unsafe_component.badge.borderWidth",
        borderRadius: "unsafe_component.badge.radius",
        ...sizeBoxProps,
        ...variantBoxProps,
        children: children
    });
};
const mapSizeToBoxProps = (size)=>({
        height: `unsafe_component.badge.sizing.${size}.height`,
        minWidth: `unsafe_component.badge.sizing.${size}.height`,
        typography: `unsafe_component.badge.typography.${size}`
    });
const mapVariantToBoxProps = (variant, isDisabled)=>{
    const variantParts = variant.split('-');
    const name = variantParts[0];
    const backgroundBehavior = variantParts[1] || 'default';
    if (isDisabled) {
        return {
            backgroundColor: `unsafe_component.badge.color.${name}.${backgroundBehavior}.bgDisabled`,
            color: `unsafe_component.badge.color.${name}.${backgroundBehavior}.contentDisabled`,
            borderColor: `unsafe_component.badge.color.${name}.${backgroundBehavior}.borderDisabled`
        };
    }
    return {
        backgroundColor: `unsafe_component.badge.color.${name}.${backgroundBehavior}.bg`,
        color: `unsafe_component.badge.color.${name}.${backgroundBehavior}.content`,
        borderColor: `unsafe_component.badge.color.${name}.${backgroundBehavior}.border`
    };
};

/**
 * Custom hook to map `Button` props to `Box` ones with all needed interactive and accessible HTML attributes.
 * @param ref Button element ref
 * @param props Button props
 * @returns Box props
 */ const useButton = (ref, props)=>{
    /**
	 * The internal implementation is heavily inspired by the react-aria one (v3.6.4) with custom adaptation.
	 * The hook contract is independent from the `react-aria` one to not introduce tight-coupling
	 * and allow switching to other a11y libraries more easily if needed.
	 * @see `react-aria` source used for the implementation https://github.com/adobe/react-spectrum/blob/6e30b8786903b522435dab612f84b3377d8ab08c/packages/%40react-aria/button/src/useButton.ts
	 */ const refObject = useObjectRef(ref);
    const { isDisabled = false, onPress, onPressStart, onPressEnd, onFocus, onBlur, onKeyDown, onKeyUp, onHoverStart, onHoverEnd, onHoverChange, href, type = 'button', preventFocusOnPress, excludeFromTabOrder, onFocusChange, allowFocusWhenDisabled } = props;
    const { boxProps: focusableProps, isFocusVisible } = useFocusable(refObject, {
        isDisabled,
        excludeFromTabOrder,
        onFocus,
        onFocusChange,
        onBlur,
        onKeyDown,
        onKeyUp
    });
    const { boxProps: hoverProps, isHovered } = useHoverable({
        isDisabled,
        onHoverStart,
        onHoverEnd,
        onHoverChange
    });
    let additionalProps;
    const isLink = typeof href === 'string';
    /**
	 * From the initial `react-aria` implementation and following YAGNI principle (to mitigate bundle size cost),
	 * the logic tied to `elementType === 'input'` has been removed (no use case right now).
	 * It focuses only in `elementType === 'a'` for non button html tag.
	 * Some custom logic has been added as well to use `ButtonProps` as input instead of HTML attributes
	 * (eg. the `as` prop, or `isExternal` prop used instead of the `target` one in `react-aria`)
	 */ if (!isLink) {
        additionalProps = {
            type,
            disabled: isDisabled,
            as: 'button'
        };
    } else {
        additionalProps = {
            tabIndex: isDisabled ? undefined : 0,
            href: isDisabled ? undefined : href,
            'aria-disabled': !isDisabled ? undefined : isDisabled,
            as: 'a',
            target: props.isExternal ? '_blank' : undefined,
            /**
			 * `noopener` for security purposes (to prevent the opening page from gaining access to the document opening it)
			 * `noreferrer` has not been added since it can negative consequences on SEO.
			 * @todo Confirm with the SEO team, the `noreferrer`/`nofollow` use cases if any valid ones.
			 */ rel: props.isExternal ? 'noopener' : undefined
        };
    }
    const { boxProps: pressProps, isPressed } = usePressable({
        onPress,
        onPressStart,
        onPressEnd,
        isDisabled,
        preventFocusOnPress
    });
    return {
        boxProps: mergeProps$1(additionalProps, {
            ...focusableProps,
            tabIndex: allowFocusWhenDisabled && isDisabled ? -1 : focusableProps.tabIndex
        }, pressProps, hoverProps, {
            ref: refObject
        }),
        isHovered,
        isPressed,
        isFocused: isFocusVisible,
        isLink
    };
};

const spin = unsafe_keyframes({
    from: {
        transform: 'rotate(0deg)'
    },
    to: {
        transform: 'rotate(360deg)'
    }
});
// Special forwardRef + `as` at the end to ensure good typing about generics
const Button = /*#__PURE__*/ forwardRef(function Button(props, ref) {
    const { children, isDisabled, variant = 'primary', size = '48', href, onPress, onPressStart, onPressEnd, isExternal, icon, iconOnly = false, iconPosition = 'start', badgeProps, isFullWidth = false, preventFocusOnPress, excludeFromTabOrder, onFocus, onFocusChange, onBlur, onKeyDown, onKeyUp, isLoading = false, 'aria-label': ariaLabel, position = 'relative', type, ...containerProps } = props;
    const computedVariant = variant.split('-')[0];
    const backgroundBehavior = variant.split('-')[1] || 'default';
    const { boxProps, isHovered, isPressed, isLink, isFocused } = useButton(ref, props);
    const tokens = useTokens();
    const LinkComponent = useRenderLink({
        isExternal
    });
    const Component = isLink ? LinkComponent : Box;
    const stateProps = useMemo(()=>mapButtonStateToBoxProps({
            isDisabled,
            isHovered,
            isPressed,
            variant: computedVariant,
            backgroundBehavior
        }), [
        isDisabled,
        isHovered,
        isPressed,
        computedVariant,
        backgroundBehavior
    ]);
    const buttonWidth = useMemo(()=>mapButtonWidthProps({
            iconOnly,
            isFullWidth,
            size
        }), [
        iconOnly,
        isFullWidth,
        size
    ]);
    const buttonStyleProps = {
        minWidth: useMemo(()=>mapButtonMinWidthProps({
                iconOnly,
                size
            }), [
            iconOnly,
            size
        ]),
        paddingHorizontal: useMemo(()=>mapButtonPaddingProps('horizontal', tokens, size, iconOnly), [
            tokens,
            size,
            iconOnly
        ]),
        paddingVertical: useMemo(()=>mapButtonPaddingProps('vertical', tokens, size, iconOnly), [
            tokens,
            size,
            iconOnly
        ])
    };
    const iconProps = {
        icon,
        iconOnly,
        iconPosition,
        size,
        isLoading,
        margin: useMemo(()=>mapIconMarginProps({
                iconOnly,
                iconPosition,
                size
            }), [
            iconOnly,
            iconPosition,
            size
        ])
    };
    const displayChildren = useMemo(()=>mapChildrenDisplay({
            iconOnly
        }), [
        iconOnly
    ]);
    const computedChildren = Array.isArray(children) ? children.join('') : children;
    return /*#__PURE__*/ jsxs(Component, {
        ...containerProps,
        ...boxProps,
        width: buttonWidth,
        display: "inline-flex",
        position: position,
        padding: "spacing.none",
        borderWidth: "borderWidth.none",
        borderStyle: "none",
        borderRadius: "unsafe_component.button.radius",
        typography: "unsafe_component.button.typography",
        // Outline only on Keyboard focus (focus-visible)
        outlineStyle: "none",
        cursor: stateProps.cursor,
        "aria-label": ariaLabel ?? (iconOnly ? computedChildren : undefined),
        "aria-busy": isLoading ? 'true' : undefined,
        children: [
            /*#__PURE__*/ jsx(Box, {
                display: "inline-flex",
                borderRadius: "unsafe_component.button.radius",
                height: `calc(${tokens[`unsafe_component.button.sizing.${size}`]} + (2 * ${tokens[`unsafe_component.button.spacing.${size}.touchZone`]}))`,
                position: "absolute",
                left: 0,
                right: 0,
                top: `-unsafe_component.button.spacing.${size}.touchZone`
            }),
            /*#__PURE__*/ jsxs(Box, {
                ...mergeProps$1(stateProps, buttonStyleProps),
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                borderStyle: "solid",
                // Outline only on Keyboard focus (focus-visible)
                outlineStyle: isFocused ? 'solid' : 'none',
                outlineColor: [
                    'Highlight',
                    '-webkit-focus-ring-color'
                ],
                outlineWidth: "borderWidth.2",
                outlineOffset: "-borderWidth.2",
                borderRadius: "unsafe_component.button.radius",
                borderWidth: "unsafe_component.button.borderWidth",
                transitionDuration: "unsafe_component.button.motionDuration.button",
                transitionProperty: "background-color",
                transitionTimingFunction: "unsafe_component.button.motionEasing.button",
                height: `unsafe_component.button.sizing.${size}`,
                width: "100%",
                overflow: [
                    'hidden',
                    'clip'
                ],
                boxShadow: backgroundBehavior === 'floating' ? 'unsafe_component.button.shadow' : undefined,
                children: [
                    iconPosition === 'start' ? /*#__PURE__*/ jsx(IconElement, {
                        ...iconProps
                    }) : null,
                    iconOnly !== true && /*#__PURE__*/ jsx(Box, {
                        as: "span",
                        display: displayChildren,
                        whiteSpace: "nowrap",
                        overflow: [
                            'hidden',
                            'clip'
                        ],
                        textOverflow: "ellipsis",
                        opacity: isLoading ? 'opacity.none' : undefined,
                        children: children
                    }),
                    iconPosition === 'end' ? /*#__PURE__*/ jsx(IconElement, {
                        ...iconProps
                    }) : null,
                    /*#__PURE__*/ jsx(ButtonBadge, {
                        size: size,
                        variant: variant,
                        isDisabled: isDisabled,
                        badgeProps: badgeProps
                    }),
                    /*#__PURE__*/ jsx(Spinner, {
                        isLoading: isLoading
                    })
                ]
            })
        ]
    });
});
const ButtonBadge = ({ variant, size, isDisabled, badgeProps })=>{
    const badgeVariant = useMemo(()=>mapButtonVariantToBadge(variant), [
        variant
    ]);
    return badgeProps?.label && badgeProps?.children ? /*#__PURE__*/ jsx(Badge, {
        ...badgeProps,
        isDisabled: isDisabled,
        variant: badgeVariant,
        marginLeft: `unsafe_component.button.spacing.${size}.default.gap`,
        children: badgeProps.children
    }) : null;
};
const Spinner = ({ isLoading })=>isLoading ? /*#__PURE__*/ jsx(Box, {
        as: "span",
        position: "absolute",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        children: /*#__PURE__*/ jsx(LoaderIcon, {
            color: "inherit",
            size: "sizing.20",
            animationDuration: "unsafe_component.button.motionDuration.spinner",
            animationTimingFunction: "unsafe_component.button.motionEasing.spinner",
            animationName: spin,
            animationIterationCount: "infinite"
        })
    }) : null;
const mapIconMarginProps = ({ iconPosition, iconOnly, size })=>{
    const key = `margin${iconPosition === 'start' ? 'Right' : 'Left'}`;
    if (typeof iconOnly === 'object') {
        return {
            [key]: mapResponsive(iconOnly, [
                'initial',
                `unsafe_component.button.spacing.${size}.default.gap`
            ])
        };
    }
    if (iconOnly) {
        return {};
    }
    return {
        [key]: `unsafe_component.button.spacing.${size}.default.gap`
    };
};
const IconElement = ({ icon: Icon, isLoading, margin, size })=>{
    if (!Icon) return null;
    return /*#__PURE__*/ jsx(Box, {
        as: "span",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: isLoading ? 'opacity.none' : undefined,
        children: /*#__PURE__*/ jsx(Icon, {
            size: size === '32' ? 'sizing.20' : 'sizing.24',
            ...margin
        })
    });
};
const mapButtonVariantToBadge = (variant)=>{
    switch(variant){
        case 'primary-inverted':
            return 'primary';
        case 'primary-onPrimary':
            return 'primary';
        case 'primary-onSecondary':
            return 'primary';
        case 'secondary':
            return 'primary';
        case 'secondary-floating':
            return 'primary';
        case 'tertiary':
            return 'primary';
        default:
            return 'primary-inverted';
    }
};
const mapButtonStateToBoxProps = ({ isDisabled, isHovered, isPressed, variant, backgroundBehavior })=>{
    const getBackgroundColor = ()=>{
        switch(true){
            case isDisabled:
                return `unsafe_component.button.color.${variant}.${backgroundBehavior}.bgDisabled`;
            case isPressed:
                return `unsafe_component.button.color.${variant}.${backgroundBehavior}.bgPressed`;
            case isHovered:
                return `unsafe_component.button.color.${variant}.${backgroundBehavior}.bgHover`;
            default:
                return `unsafe_component.button.color.${variant}.${backgroundBehavior}.bg`;
        }
    };
    return {
        backgroundColor: getBackgroundColor(),
        borderColor: `unsafe_component.button.color.${variant}.${backgroundBehavior}.border${isDisabled ? 'Disabled' : ''}`,
        color: `unsafe_component.button.color.${variant}.${backgroundBehavior}.text${isDisabled ? 'Disabled' : ''}`,
        cursor: isDisabled ? 'not-allowed' : 'pointer'
    };
};
const mapResponsive = (object, [conditionFulfilled, conditionUnfulfilled])=>{
    return Object.entries(object).reduce((acc, [breakpoint, condition])=>{
        return {
            ...acc,
            [breakpoint]: condition ? conditionFulfilled : conditionUnfulfilled
        };
    }, {});
};
const mapButtonPaddingProps = (direction, tokens, size, iconOnly)=>{
    const padding = `calc(${tokens[`unsafe_component.button.spacing.${size}.default.padding.${direction}`]} - ${tokens['unsafe_component.button.borderWidth']})`;
    const paddingIconOnly = `calc(${tokens[`unsafe_component.button.spacing.${size}.iconOnly.padding`]} - ${tokens['unsafe_component.button.borderWidth']})`;
    if (typeof iconOnly === 'object') {
        return mapResponsive(iconOnly, [
            paddingIconOnly,
            padding
        ]);
    }
    if (iconOnly) {
        return paddingIconOnly;
    }
    return padding;
};
const mapButtonWidthProps = ({ iconOnly, isFullWidth = false, size })=>{
    if (typeof iconOnly === 'object') {
        return mapResponsive(iconOnly, [
            `unsafe_component.button.sizing.${size}`,
            'initial'
        ]);
    }
    if (iconOnly) {
        return `unsafe_component.button.sizing.${size}`;
    }
    // iconOnly has priority for now
    if (!isFullWidth) {
        return undefined;
    }
    if (typeof isFullWidth === 'boolean') {
        return '100%';
    }
    return mapResponsive(isFullWidth, [
        '100%',
        'initial'
    ]);
};
const mapButtonMinWidthProps = ({ iconOnly, size })=>{
    if (typeof iconOnly === 'object') {
        return mapResponsive(iconOnly, [
            `unsafe_component.button.sizing.${size}`,
            'initial'
        ]);
    }
    if (iconOnly) {
        return `unsafe_component.button.sizing.${size}`;
    }
    return 'initial';
};
const mapChildrenDisplay = ({ iconOnly })=>{
    if (typeof iconOnly === 'object') {
        return mapResponsive(iconOnly, [
            'none',
            'inline'
        ]);
    }
    if (iconOnly) {
        return 'none';
    }
    return 'inline';
};

const ButtonBar = ({ displayMode = 'page', direction, startButtonProps, endButtonProps, ...restProps })=>{
    const responsiveDirection = useMemo(()=>mapButtonBarDirectionProps({
            direction
        }), [
        direction
    ]);
    const isStartButtonFullWidth = !!startButtonProps?.isFullWidth;
    const isEndButtonFullWidth = !!endButtonProps?.isFullWidth;
    // Prevent issue, where only one button get isFullWidth, while both buttons are defined
    const isButtonFullWidth = isStartButtonFullWidth && isEndButtonFullWidth || isStartButtonFullWidth && endButtonProps === undefined || isEndButtonFullWidth && startButtonProps === undefined;
    return /*#__PURE__*/ jsxs(Box, {
        ...restProps,
        as: "div",
        display: "flex",
        flexDirection: responsiveDirection,
        justifyContent: startButtonProps !== undefined && endButtonProps !== undefined ? 'space-between' : startButtonProps !== undefined ? 'flex-start' : 'flex-end',
        alignItems: "center",
        gap: "unsafe_component.buttonBar.spacing.gap",
        paddingVertical: {
            'breakpoint.base': 'unsafe_component.buttonBar.spacing.phone.padding.vertical',
            'breakpoint.md': `unsafe_component.buttonBar.spacing.${displayMode}.padding.vertical`
        },
        paddingHorizontal: {
            'breakpoint.base': 'unsafe_component.buttonBar.spacing.phone.padding.horizontal',
            'breakpoint.md': `unsafe_component.buttonBar.spacing.${displayMode}.padding.horizontal`
        },
        width: "100%",
        borderStyle: "solid",
        borderColor: "unsafe_component.buttonBar.color.border",
        borderWidth: "borderWidth.none",
        borderTopWidth: "unsafe_component.buttonBar.borderWidth",
        backgroundColor: "unsafe_component.buttonBar.color.bg",
        children: [
            startButtonProps !== undefined && /*#__PURE__*/ jsx(Button, {
                ...startButtonProps,
                variant: "tertiary",
                isFullWidth: {
                    'breakpoint.base': true,
                    'breakpoint.md': isButtonFullWidth ? true : false
                },
                size: "48",
                iconOnly: false
            }),
            endButtonProps !== undefined && /*#__PURE__*/ jsx(Button, {
                ...endButtonProps,
                variant: "primary",
                isFullWidth: {
                    'breakpoint.base': true,
                    'breakpoint.md': isButtonFullWidth ? true : false
                },
                size: "48",
                iconOnly: false
            })
        ]
    });
};
const mapButtonBarDirectionProps = ({ direction = 'horizontal' })=>{
    // if we don't have direction return undefined
    if (!direction) {
        return undefined;
    }
    // if the prop is not an responsive object return the right value associated
    if (typeof direction === 'string') {
        return direction === 'vertical' ? 'column-reverse' : 'row';
    }
    return Object.entries(direction).reduce((acc, [breakpoint, direction])=>{
        return {
            ...acc,
            // apply column-reverse for direction vertical or default row otherwise
            [breakpoint]: direction === 'vertical' ? 'column-reverse' : 'row'
        };
    }, {});
};

const ALLOWED_TITLE_TAGS = [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'span'
];
/**
 * "span" in this context still acts as a semantic heading with `role="heading"`,
 * and a default `aria-level=2` to ensure accessibility while preserving semantic structure.
 * This is useful for situations where the heading hierarchy needs to be preserved, or for custom use cases.
 *
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/heading_role#associated_wai-aria_roles_states_and_properties
 */ const ALLOWED_TITLE_TAGS_TO_ARIA_LEVELS = {
    h1: 1,
    h2: 2,
    h3: 3,
    h4: 4,
    h5: 5,
    h6: 6,
    span: 2
};
const isAllowedTitleTag = (tag)=>ALLOWED_TITLE_TAGS.includes(tag);

const TopBarFloatingButton = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { children, icon: Icon, isDisabled = false, onPress, ...containerProps } = props;
    const { boxProps, isHovered, isPressed, isFocused } = useButton(ref, props);
    const stateProps = useMemo(()=>mapTopBarFloatingButtonStateToBoxProps({
            isDisabled,
            isHovered,
            isPressed
        }), [
        isDisabled,
        isHovered,
        isPressed
    ]);
    const computedChildren = Array.isArray(children) ? children.join('') : children;
    return /*#__PURE__*/ jsx(Box, {
        ...mergeProps$1(containerProps, boxProps, stateProps),
        display: "inline-flex",
        borderWidth: "borderWidth.none",
        borderRadius: "unsafe_component.topBar.radius.floatingButton",
        boxShadow: "unsafe_component.topBar.shadow.floatingButton",
        padding: "unsafe_component.topBar.spacing.floatingButton",
        width: "unsafe_component.topBar.sizing.floatingButton",
        height: "unsafe_component.topBar.sizing.floatingButton",
        "aria-label": computedChildren,
        // Outline only on Keyboard focus (focus-visible)
        outlineStyle: isFocused ? 'auto' : 'none',
        children: /*#__PURE__*/ jsx(Icon, {
            size: "sizing.24"
        })
    });
});
const mapTopBarFloatingButtonStateToBoxProps = ({ isDisabled, isHovered, isPressed })=>{
    const getBackgroundColor = ()=>{
        switch(true){
            case isDisabled:
                return `unsafe_component.topBar.color.floatingButton.bgDisabled`;
            case isPressed:
                return `unsafe_component.topBar.color.floatingButton.bgPressed`;
            case isHovered:
                return `unsafe_component.topBar.color.floatingButton.bgHover`;
            default:
                return `unsafe_component.topBar.color.floatingButton.bg`;
        }
    };
    return {
        backgroundColor: getBackgroundColor(),
        color: `unsafe_component.topBar.color.floatingButton.content${isDisabled ? 'Disabled' : ''}`,
        cursor: isDisabled ? 'not-allowed' : 'pointer'
    };
};

const TopBar = (props)=>{
    return /*#__PURE__*/ jsx(TopBarBase, {
        ...props
    });
};
/**
 * Internal usage only
 */ const TopBarBase = ({ titleId, title, largeTitle, titleAs, largeTitleAs, isOnPicture, startButtonProps, endButtonsProps, hasBottomBorder = true, ...restProps })=>{
    const tokens = useTokens();
    return /*#__PURE__*/ jsx(Box, {
        ...restProps,
        as: "div",
        display: "flex",
        flexDirection: "column",
        alignItems: "left",
        width: "100%",
        borderBottomWidth: isOnPicture ? 'borderWidth.none' : 'unsafe_component.topBar.borderWidth.divider',
        borderBottomColor: hasBottomBorder ? 'unsafe_component.topBar.color.border' : 'color.surface.transparent.default',
        borderBottomStyle: "solid",
        paddingVertical: `unsafe_component.topBar.spacing.padding.${isOnPicture ? 'onPicture' : 'default'}.vertical`,
        paddingBottom: hasBottomBorder ? `calc(${tokens['unsafe_component.topBar.spacing.padding.default.vertical']} - ${tokens['unsafe_component.topBar.borderWidth.divider']})` : undefined,
        paddingHorizontal: `unsafe_component.topBar.spacing.padding.${isOnPicture ? 'onPicture' : 'default'}.horizontal`,
        children: isOnPicture ? /*#__PURE__*/ jsx(TopBarOnPicture, {
            startButtonProps: startButtonProps,
            endButtonsProps: endButtonsProps,
            isOnPicture: true
        }) : /*#__PURE__*/ jsx(TopBarDefault, {
            titleId: titleId,
            titleAs: titleAs,
            largeTitleAs: largeTitleAs,
            startButtonProps: startButtonProps,
            endButtonsProps: endButtonsProps,
            title: title,
            largeTitle: largeTitle
        })
    });
};
const TopBarOnPicture = ({ startButtonProps, endButtonsProps })=>/*#__PURE__*/ jsxs(Box, {
        as: "div",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: startButtonProps ? endButtonsProps ? 'space-between' : 'flex-start' : 'flex-end',
        children: [
            startButtonProps ? /*#__PURE__*/ jsx(TopBarFloatingButton, {
                ...startButtonProps,
                marginRight: Array.isArray(endButtonsProps) && endButtonsProps.length > 0 ? 'unsafe_component.topBar.spacing.gap.header.onPicture' : undefined,
                children: startButtonProps.children
            }) : null,
            Array.isArray(endButtonsProps) ? /*#__PURE__*/ jsx(Box, {
                as: "div",
                display: "inline-flex",
                alignItems: "center",
                children: endButtonsProps.map((endButton, index)=>/*#__PURE__*/ createElement(TopBarFloatingButton, {
                        ...endButton,
                        key: index,
                        marginRight: index < endButtonsProps.length - 1 ? 'unsafe_component.topBar.spacing.gap.header.onPicture' : undefined
                    }, endButton.children))
            }) : null
        ]
    });
const TopBarDefault = ({ titleId, title, titleAs, startButtonProps, endButtonsProps, largeTitle, largeTitleAs })=>{
    const endTextButton = endButtonsProps && !Array.isArray(endButtonsProps) && /*#__PURE__*/ jsx(Button, {
        ...endButtonsProps,
        variant: "secondary",
        size: "40",
        children: endButtonsProps.children
    });
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsxs(Box, {
                as: "div",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: title || startButtonProps ? 'space-between' : 'flex-end',
                paddingRight: endTextButton ? 'unsafe_component.topBar.spacing.padding.default.horizontal' : undefined,
                children: [
                    title || startButtonProps ? /*#__PURE__*/ jsxs(Box, {
                        as: "div",
                        display: "inline-flex",
                        alignItems: "center",
                        overflow: [
                            'hidden',
                            'clip'
                        ],
                        minHeight: "unsafe_component.topBar.sizing.minHeight",
                        marginRight: endButtonsProps ? 'unsafe_component.topBar.spacing.gap.header.default' : undefined,
                        children: [
                            startButtonProps ? /*#__PURE__*/ jsx(Button, {
                                ...startButtonProps,
                                iconOnly: true,
                                size: "40",
                                variant: "tertiary",
                                children: startButtonProps.children
                            }) : null,
                            title ? /*#__PURE__*/ jsx(TopBarTitle, {
                                titleId: titleId,
                                titleAs: titleAs,
                                title: title
                            }) : null
                        ]
                    }) : null,
                    Array.isArray(endButtonsProps) ? /*#__PURE__*/ jsx(Box, {
                        as: "div",
                        display: "inline-flex",
                        alignItems: "center",
                        children: endButtonsProps.map((endButton, index)=>/*#__PURE__*/ createElement(Button, {
                                ...endButton,
                                iconOnly: true,
                                key: index,
                                variant: "tertiary",
                                size: "40",
                                marginRight: index < endButtonsProps.length - 1 ? 'unsafe_component.topBar.spacing.gap.header.default' : undefined
                            }, endButton.children))
                    }) : null,
                    endTextButton
                ]
            }),
            largeTitle ? /*#__PURE__*/ jsx(TopBarLargeTitle, {
                largeTitleAs: largeTitleAs,
                largeTitle: largeTitle
            }) : null
        ]
    });
};
const TopBarLargeTitle = ({ largeTitle, largeTitleAs })=>{
    const largeTitleElement = largeTitleAs !== undefined && isAllowedTitleTag(largeTitleAs) ? largeTitleAs : 'span';
    return /*#__PURE__*/ jsx(Box, {
        as: largeTitleElement,
        typography: "unsafe_component.topBar.typography.largeTitle",
        paddingHorizontal: "unsafe_component.topBar.spacing.padding.default.horizontal",
        // Reset margin to avoid spacing from choosen heading level
        margin: "spacing.none",
        marginTop: "unsafe_component.topBar.spacing.gap.title",
        children: largeTitle
    });
};
const TopBarTitle = ({ titleId, title, titleAs })=>{
    const titleElement = titleAs !== undefined && isAllowedTitleTag(titleAs) ? titleAs : 'span';
    return /*#__PURE__*/ jsx(Box, {
        as: titleElement,
        id: titleId,
        typography: "unsafe_component.topBar.typography.title",
        flexGrow: 1,
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: [
            'hidden',
            'clip'
        ],
        // Reset margin to avoid spacing from choosen heading level
        margin: "spacing.none",
        marginLeft: "unsafe_component.topBar.spacing.gap.header.default",
        children: title
    });
};

const ModalDialog = (props)=>{
    const { id, hasCloseButton, title, titleAs, headerButtonProps, hasContentPadding, children, footerStartButtonProps, footerEndButtonProps, footerMobileDirection, displayMode, dialogRef, contentRef, ...restProps } = props;
    const [showTopBorder, setShowTopBorder] = useState(false);
    const { boxProps, close = ()=>{} } = useModalDialog(dialogRef, {
        id
    });
    const hasTopBar = Boolean(title || hasCloseButton || headerButtonProps);
    const updateShowTopBorder = ()=>{
        if (contentRef.current && showTopBorder !== contentRef.current?.scrollTop > 0) {
            setShowTopBorder(!showTopBorder);
        }
    };
    useResizeObserver(contentRef, {
        onResize: updateShowTopBorder
    });
    return /*#__PURE__*/ jsxs(Box, {
        ...mergeProps$1(restProps, boxProps.dialog),
        ref: dialogRef,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        outlineStyle: "none",
        flex: "1 1 0%",
        minHeight: 0,
        children: [
            hasTopBar ? /*#__PURE__*/ jsx(ModalDialogTopBar, {
                titleId: boxProps.title.id,
                title: title,
                titleAs: titleAs,
                hasCloseButton: hasCloseButton,
                headerButtonProps: headerButtonProps,
                displayMode: displayMode,
                showTopBorder: showTopBorder,
                close: close
            }) : null,
            /*#__PURE__*/ jsx(Box, {
                tabIndex: 0,
                as: "div",
                ref: contentRef,
                onScroll: updateShowTopBorder,
                paddingHorizontal: hasContentPadding ? {
                    'breakpoint.base': `unsafe_component.modal.spacing.padding.phone.${displayMode}.horizontal`,
                    'breakpoint.md': `unsafe_component.modal.spacing.padding.desktop.${displayMode}.horizontal`
                } : undefined,
                paddingBottom: hasContentPadding ? {
                    'breakpoint.base': `unsafe_component.modal.spacing.padding.phone.${displayMode}.bottom`,
                    'breakpoint.md': `unsafe_component.modal.spacing.padding.desktop.${displayMode}.bottom`
                } : undefined,
                paddingTop: hasContentPadding ? {
                    'breakpoint.base': `unsafe_component.modal.spacing.padding.phone.${displayMode}.top${!hasTopBar ? 'WithoutTopBar' : ''}`,
                    'breakpoint.md': `unsafe_component.modal.spacing.padding.desktop.${displayMode}.top${!hasTopBar ? 'WithoutTopBar' : ''}`
                } : undefined,
                overflow: "auto",
                flex: "1 1 0%",
                minHeight: 0,
                children: children
            }),
            /*#__PURE__*/ jsx(ModalDialogBottomBar, {
                ...props,
                close: close
            })
        ]
    });
};
const ModalDialogTopBar = ({ titleId, title, titleAs, hasCloseButton, headerButtonProps, displayMode, showTopBorder, close })=>{
    const t = useTranslation();
    return /*#__PURE__*/ jsx(TopBarBase, {
        titleId: titleId,
        title: title,
        titleAs: titleAs,
        startButtonProps: hasCloseButton && close ? {
            icon: CloseIcon,
            onPress: ()=>close(),
            children: t('core::close')
        } : undefined,
        endButtonsProps: typeof headerButtonProps === 'function' ? headerButtonProps(close) : headerButtonProps,
        hasBottomBorder: displayMode === 'fullscreen' || showTopBorder
    });
};
const ModalDialogBottomBar = ({ footerStartButtonProps, footerEndButtonProps, close, footerMobileDirection })=>{
    return footerStartButtonProps || footerEndButtonProps ? /*#__PURE__*/ jsx(ButtonBar, {
        startButtonProps: typeof footerStartButtonProps === 'function' ? footerStartButtonProps(close) : footerStartButtonProps,
        endButtonProps: typeof footerEndButtonProps === 'function' ? footerEndButtonProps(close) : footerEndButtonProps,
        displayMode: "modal",
        direction: footerMobileDirection ? {
            'breakpoint.base': footerMobileDirection,
            'breakpoint.xs': 'horizontal'
        } : undefined
    }) : null;
};

const Modal = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { contentRef: userContentRef, displayMode = 'default', predefinedWidth = 'default', children, isDismissable = false, isKeyboardDismissDisabled = false, isOpen, defaultOpen, onOpenChange, trigger, hasCloseButton = true, title, titleAs, headerButtonProps, footerStartButtonProps, footerEndButtonProps, footerMobileDirection, hasContentPadding = true, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, ...restProps } = props;
    const dialogRef = useObjectRef(ref);
    const contentRef = useObjectRef(userContentRef);
    const { boxProps, state, refs, isExiting } = useModal(props);
    const { id, ...overlayProps } = boxProps.overlay;
    return /*#__PURE__*/ jsxs(ModalContext.Provider, {
        value: {
            state
        },
        children: [
            trigger(boxProps.trigger),
            /*#__PURE__*/ jsx(Overlay, {
                isOpen: state.isOpen,
                isExiting: isExiting,
                zIndex: "zIndex.3",
                children: /*#__PURE__*/ jsx(ModalWrapper, {
                    isDismissable: isDismissable,
                    isKeyboardDismissDisabled: isKeyboardDismissDisabled,
                    displayMode: displayMode,
                    predefinedWidth: predefinedWidth,
                    underlayRef: refs.underlay,
                    ref: refs.wrapper,
                    isExiting: isExiting,
                    ...mergeProps$1(restProps, overlayProps),
                    children: /*#__PURE__*/ jsx(ModalDialog, {
                        id: id,
                        dialogRef: dialogRef,
                        contentRef: contentRef,
                        hasCloseButton: hasCloseButton,
                        title: title,
                        titleAs: titleAs,
                        headerButtonProps: headerButtonProps,
                        footerStartButtonProps: footerStartButtonProps,
                        footerEndButtonProps: footerEndButtonProps,
                        footerMobileDirection: footerMobileDirection,
                        hasContentPadding: hasContentPadding,
                        displayMode: displayMode,
                        "aria-label": ariaLabel,
                        "aria-labelledby": ariaLabelledBy,
                        children: typeof children === 'function' ? children(state.close) : children
                    })
                })
            })
        ]
    });
});

const useFieldTranslation = ()=>useTranslation({
        'en-GB': {
            'field::label::optional': 'optional'
        },
        'fr-FR': {
            'field::label::optional': 'facultatif'
        },
        'de-DE': {
            'field::label::optional': 'Optional'
        },
        'nl-NL': {
            'field::label::optional': 'optioneel'
        }
    });

const FieldNecessityIndicator = ({ isOptional, marginLeft, color, typography })=>{
    const t = useFieldTranslation();
    return /*#__PURE__*/ jsx(Box, {
        as: "span",
        marginLeft: marginLeft,
        color: isOptional ? color : undefined,
        typography: isOptional ? typography : undefined,
        children: isOptional ? `(${t('field::label::optional')})` : '*'
    });
};

/**
 * Custom hook to map Tooltip and TooltipTrigger props to `Box` ones with all needed interactive and accessible HTML attributes.
 * @param ref Tooltip ref and TooltipTrigger ref
 * @returns Tooltip and TooltipTrigger props, TooltipTrigger state and isExiting for animation
 */ const useTooltipWrapper = ({ tooltip, trigger })=>{
    const tokens = useTokens();
    const state = useTooltipTriggerState({
        delay: parseFloat(tokens['motionDuration.300']),
        closeDelay: 0
    });
    const { triggerProps, tooltipProps: triggerTooltipProps } = useTooltipTrigger$1({}, state, trigger);
    const isExiting = useExitAnimation(tooltip, state.isOpen);
    const { boxProps: pressProps } = usePressable({
        onPressStart: ()=>{
            if (state.isOpen) state.close(true);
            else state.open(true);
        }
    });
    return {
        boxProps: {
            trigger: mergeProps$1(triggerProps, pressProps),
            tooltip: triggerTooltipProps
        },
        state,
        isExiting
    };
};
const useTooltip = ({ trigger, tooltip }, { tooltipPlacement, tooltipAlignment, offset, state })=>{
    const rootFontSizeValue = getRootFontSizeValue();
    const arrowDiagonal = 18 * rootFontSizeValue / 16;
    const arrowSize = arrowDiagonal / Math.sqrt(2);
    const isHorizontal = tooltipPlacement === 'left' || tooltipPlacement === 'right';
    let computedPlacement;
    if (isHorizontal && tooltipAlignment === 'start') computedPlacement = `${tooltipPlacement} top`;
    else if (isHorizontal && tooltipAlignment === 'end') computedPlacement = `${tooltipPlacement} bottom`;
    else if (!isHorizontal && tooltipAlignment !== 'middle') computedPlacement = `${tooltipPlacement} ${tooltipAlignment}`;
    else computedPlacement = `${tooltipPlacement}`;
    const { overlayProps, arrowProps, placement } = useOverlayPosition({
        placement: computedPlacement,
        targetRef: trigger,
        overlayRef: tooltip,
        offset: arrowDiagonal / 2 + (offset ? offset(rootFontSizeValue) : 0),
        isOpen: state.isOpen
    });
    const isEntering = useEnterAnimation(tooltip, !!placement);
    const { tooltipProps } = useTooltip$1({}, state);
    return {
        boxProps: {
            tooltip: mergeProps$1(tooltipProps, {
                unsafe_style: overlayProps.style
            }),
            arrow: mergeProps$1(arrowProps, {
                unsafe_style: arrowProps.style
            })
        },
        placement: !placement || placement === 'center' ? 'bottom' : placement,
        arrowDiagonal,
        arrowSize,
        isEntering
    };
};
const useTooltipTrigger = (ref)=>{
    return useFocusable(ref);
};

const fadeAndSlide = (placement)=>unsafe_keyframes({
        from: {
            opacity: 0,
            transform: placement === 'top' || placement === 'bottom' ? 'translateY(4px)' : 'translateX(4px)'
        },
        to: {
            opacity: 1,
            transform: placement === 'top' || placement === 'bottom' ? 'translateY(0)' : 'translateX(0)'
        }
    });
const Tooltip = ({ children, ...props })=>{
    const tooltipRef = useRef(null);
    const triggerRef = useRef(null);
    const { boxProps: { trigger, tooltip }, state, isExiting } = useTooltipWrapper({
        tooltip: tooltipRef,
        trigger: triggerRef
    });
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx(FocusableProvider, {
                ...trigger,
                ref: triggerRef,
                position: "relative",
                children: children
            }),
            /*#__PURE__*/ jsx(Overlay, {
                isOpen: state.isOpen,
                isExiting: isExiting,
                children: /*#__PURE__*/ jsx(TooltipInner, {
                    ...mergeProps$1(props, tooltip),
                    isExiting: isExiting,
                    tooltipRef: tooltipRef,
                    triggerRef: triggerRef,
                    state: state
                })
            })
        ]
    });
};
const TooltipInner = ({ content, offset, placement: tooltipPlacement = 'bottom', alignment: tooltipAlignment = 'middle', isExiting, tooltipRef, triggerRef, state, ...rest })=>{
    const tokens = useTokens();
    const { boxProps: { tooltip, arrow }, placement, arrowDiagonal, arrowSize, isEntering } = useTooltip({
        trigger: triggerRef,
        tooltip: tooltipRef
    }, {
        tooltipPlacement,
        tooltipAlignment,
        offset,
        state
    });
    return /*#__PURE__*/ jsxs(Box, {
        ...mergeProps$1(rest, tooltip),
        ref: tooltipRef,
        display: "flex",
        maxWidth: "unsafe_component.tooltip.sizing.maxWidth",
        borderWidth: "unsafe_component.tooltip.borderWidth",
        borderStyle: "solid",
        borderColor: "unsafe_component.tooltip.color.border",
        backgroundColor: "unsafe_component.tooltip.color.bg",
        boxShadow: "unsafe_component.tooltip.shadow",
        borderRadius: "unsafe_component.tooltip.radius",
        color: "unsafe_component.tooltip.color.text",
        typography: "unsafe_component.tooltip.typography",
        animationDuration: "motionDuration.150",
        animationTimingFunction: isExiting ? 'motionEasing.ease-out' : 'motionEasing.ease-in',
        animationName: isEntering || isExiting ? fadeAndSlide(placement) : undefined,
        animationDirection: isExiting ? 'reverse' : undefined,
        children: [
            /*#__PURE__*/ jsx(Box, {
                ...arrow,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: placement === 'left' || placement === 'right' ? arrowDiagonal / 2 : arrowDiagonal,
                height: placement === 'top' || placement === 'bottom' ? arrowDiagonal / 2 : arrowDiagonal,
                overflow: [
                    'hidden',
                    'clip'
                ],
                transform: placement === 'top' || placement === 'bottom' ? 'translateX(-50%)' : 'translateY(-50%)',
                position: "absolute",
                top: placement === 'top' ? '100%' : undefined,
                bottom: placement === 'bottom' ? '100%' : undefined,
                left: placement === 'left' ? '100%' : undefined,
                right: placement === 'right' ? '100%' : undefined,
                children: /*#__PURE__*/ jsx(Box, {
                    width: arrowSize,
                    height: arrowSize,
                    transform: "rotate(45deg)",
                    position: "absolute",
                    top: placement === 'top' ? -arrowSize / 2 : undefined,
                    bottom: placement === 'bottom' ? -arrowSize / 2 : undefined,
                    left: placement === 'left' ? -arrowSize / 2 : undefined,
                    right: placement === 'right' ? -arrowSize / 2 : undefined,
                    borderWidth: "unsafe_component.tooltip.borderWidth",
                    borderStyle: "solid",
                    borderColor: "unsafe_component.tooltip.color.border",
                    backgroundColor: "unsafe_component.tooltip.color.bg"
                })
            }),
            /*#__PURE__*/ jsx(Box, {
                padding: `calc(${tokens['unsafe_component.tooltip.spacing.padding']} - ${tokens['unsafe_component.tooltip.borderWidth']})`,
                overflowY: "auto",
                children: content
            })
        ]
    });
};

const FieldTooltipTrigger = ()=>{
    const ref = useRef(null);
    const { boxProps } = useTooltipTrigger(ref);
    return /*#__PURE__*/ jsx(Box, {
        as: "button",
        type: "button",
        display: "inline-flex",
        marginLeft: "unsafe_component.field.spacing.margin.header.label.gap",
        padding: "unsafe_component.field.spacing.padding.tooltip",
        borderWidth: "borderWidth.none",
        ref: ref,
        ...boxProps,
        children: /*#__PURE__*/ jsx(InfoCircleIcon, {
            size: "sizing.24",
            color: "unsafe_component.field.color.tooltipIcon"
        })
    });
};

const StateMessage = ({ children, variant = 'helper', id, ...restProps })=>{
    const Icon = mapIconToVariant(variant);
    return /*#__PURE__*/ jsxs(Box, {
        ...restProps,
        id: id,
        display: "inline-flex",
        flexWrap: "nowrap",
        color: `unsafe_component.stateMessage.color.${variant}.content`,
        children: [
            variant !== 'helper' && /*#__PURE__*/ jsx(Icon, {
                size: "sizing.20"
            }),
            /*#__PURE__*/ jsx(Box, {
                as: "span",
                typography: "unsafe_component.stateMessage.typography",
                display: "flex",
                alignItems: "center",
                marginLeft: `unsafe_component.stateMessage.spacing.${variant}.gap`,
                children: children
            })
        ]
    });
};
const mapIconToVariant = (variant)=>{
    switch(variant){
        case 'error':
            return CloseFilledCircleIcon;
        case 'success':
            return CheckFilledCircleIcon;
        case 'warning':
            return ExclamationFilledCircleIcon;
        case 'info':
        default:
            return InfoFilledCircleIcon;
    }
};

const DEFAULT_LABEL_AS = 'label';

const FieldLabel = ({ as = DEFAULT_LABEL_AS, label, isDisabled, isOptional, hasNecessityIndicator, helper, helperProps, disableMargin, labelProps, hasScreenReaderHiddenLabel = false })=>{
    const tokens = useTokens();
    const isLabelString = typeof label === 'string';
    return /*#__PURE__*/ jsxs(Box, {
        srOnly: hasScreenReaderHiddenLabel,
        display: "flex",
        flexDirection: "column",
        children: [
            /*#__PURE__*/ jsxs(Box, {
                display: "inline-flex",
                alignItems: "flex-end",
                children: [
                    /*#__PURE__*/ jsxs(Box, {
                        as: as,
                        ...labelProps,
                        color: `unsafe_component.field.color.label${isDisabled ? 'Disabled' : ''}`,
                        paddingVertical: "unsafe_component.field.spacing.padding.label.vertical",
                        typography: "unsafe_component.field.typography.label",
                        children: [
                            isLabelString ? label : label.content,
                            hasNecessityIndicator ? /*#__PURE__*/ jsx(FieldNecessityIndicator, {
                                isOptional: isOptional,
                                marginLeft: "unsafe_component.field.spacing.margin.header.label.gap",
                                color: "unsafe_component.field.color.optional",
                                typography: "unsafe_component.field.typography.optional"
                            }) : null
                        ]
                    }),
                    !isLabelString && label.tooltip ? /*#__PURE__*/ jsx(Tooltip, {
                        content: label.tooltip,
                        offset: (baseFontSize)=>/**
							 * Convert px to rem and scale to the current base font size
							 */ -parseFloat(tokens['unsafe_component.field.spacing.padding.tooltip'].toString()) * baseFontSize,
                        children: /*#__PURE__*/ jsx(FieldTooltipTrigger, {})
                    }) : null
                ]
            }),
            helper && helperProps.id ? /*#__PURE__*/ jsx(StateMessage, {
                marginBottom: disableMargin ? '' : `unsafe_component.field.spacing.margin.header.helper.bottom`,
                id: helperProps.id,
                children: helper
            }) : null
        ]
    });
};

const FieldBase = /*#__PURE__*/ forwardRef(({ as = DEFAULT_LABEL_AS, children, label, helper, error, isOptional, hasNecessityIndicator = true, isDisabled, disableMargin, labelProps, helperProps, errorProps, hasScreenReaderHiddenLabel = false, ...containerProps }, ref)=>{
    const isLabelHidden = typeof label !== 'string' ? label.isHidden : false;
    return /*#__PURE__*/ jsxs(Box, {
        ...containerProps,
        ref: ref,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        children: [
            isLabelHidden && !hasScreenReaderHiddenLabel ? null : /*#__PURE__*/ jsx(FieldLabel, {
                as: as,
                label: label,
                isDisabled: isDisabled,
                isOptional: isOptional,
                hasNecessityIndicator: hasNecessityIndicator,
                helper: helper,
                helperProps: helperProps,
                disableMargin: disableMargin,
                labelProps: labelProps,
                hasScreenReaderHiddenLabel: hasScreenReaderHiddenLabel
            }),
            children,
            error && errorProps.id && !isDisabled ? /*#__PURE__*/ jsx(StateMessage, {
                marginTop: disableMargin ? '' : `unsafe_component.field.spacing.margin.footer.root.top`,
                id: errorProps.id,
                variant: "error",
                children: error
            }) : null
        ]
    });
});

const useField = ({ as = DEFAULT_LABEL_AS, label, helper, error, hasScreenReaderHiddenLabel })=>{
    const isLabelHidden = typeof label !== 'string' ? label.isHidden : false;
    const { labelProps, fieldProps: { id, 'aria-describedby': ariaDescribedBy, 'aria-labelledby': ariaLabelledBy, 'aria-label': ariaLabel }, descriptionProps, errorMessageProps } = useField$1({
        labelElementType: as,
        [isLabelHidden && !hasScreenReaderHiddenLabel ? 'aria-label' : 'label']: typeof label === 'string' ? label : label.content,
        description: helper,
        errorMessage: error
    });
    return {
        boxProps: {
            // Need to cast to BoxProps here due to conflict between `color` html prop and `color` css of Box
            label: labelProps,
            field: {
                id,
                'aria-describedby': ariaDescribedBy,
                'aria-labelledby': ariaLabelledBy,
                'aria-label': isLabelHidden && !hasScreenReaderHiddenLabel ? ariaLabel : undefined
            },
            helper: descriptionProps,
            error: errorMessageProps
        }
    };
};

// CheckboxGroup is the same as in the previous example
const CheckboxGroupContext = /*#__PURE__*/ createContext(null);
const useCheckboxGroupProvider = ()=>{
    return useContext(CheckboxGroupContext);
};

const useCheckbox = (ref, { isDisabled, error, ...rest })=>{
    const objectRef = useObjectRef(ref);
    const { boxProps: stateProps, isHovered, isFocused } = useCheckboxStates(objectRef, {
        isDisabled
    });
    const { boxProps: fieldProps } = useField({
        ...rest,
        // We need to keep children in rest for `useCheckbox` from react-aria
        label: Array.isArray(rest.children) ? rest.children.join('') : rest.children
    });
    const state = useToggleState(rest);
    const { inputProps, labelProps, isSelected, isPressed, isDisabled: isDisabledReactAria } = useCheckbox$1({
        ...rest,
        isDisabled: Boolean(isDisabled),
        isInvalid: Boolean(error)
    }, state, objectRef);
    return {
        boxProps: {
            input: mergeProps$1(inputProps, stateProps.input, {
                ref: objectRef,
                // checkbox is inside label, we only need error id link
                'aria-describedby': fieldProps.field['aria-describedby']
            }),
            checkbox: mergeProps$1(stateProps.checkbox, labelProps)
        },
        direction: 'vertical',
        isFocused,
        isPressed,
        isHovered,
        isSelected,
        isDisabled: isDisabledReactAria,
        errorId: fieldProps.error.id
    };
};
const useCheckboxInGroup = (ref, props)=>{
    const { isDisabled } = props;
    const objectRef = useObjectRef(ref);
    const { boxProps: stateProps, isHovered, isFocused } = useCheckboxStates(objectRef, {
        isDisabled
    });
    const fallbackState = useCheckboxGroupState({});
    const groupContext = useCheckboxGroupProvider();
    const state = groupContext && groupContext.state || fallbackState;
    const { inputProps, labelProps, isSelected, isPressed, isDisabled: isDisabledReactAria } = useCheckboxGroupItem({
        ...props,
        isDisabled: Boolean(isDisabled)
    }, state, objectRef);
    return {
        boxProps: {
            input: mergeProps$1(inputProps, stateProps.input, {
                ref: objectRef
            }),
            checkbox: mergeProps$1(stateProps.checkbox, labelProps)
        },
        direction: groupContext?.direction,
        isPressed,
        isHovered,
        isFocused,
        isSelected,
        isDisabled: isDisabledReactAria,
        // Used only for single checkbox
        errorId: undefined
    };
};
const useCheckboxStates = (ref, { isDisabled })=>{
    const { boxProps: hoverProps, isHovered } = useHoverable({
        isDisabled: Boolean(isDisabled)
    });
    const { boxProps: focusProps, isFocusVisible } = useFocusable(ref, {
        isDisabled,
        unsafe_ignoreProviderProps: true
    });
    return {
        boxProps: {
            input: focusProps,
            checkbox: hoverProps
        },
        isHovered,
        isFocused: isFocusVisible
    };
};

const Checkbox = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { children, isOptional, hasNecessityIndicator = true, tooltip, hasBorder = false, error, isDisabled, isFocused, isSelected, defaultSelected, name, onChange, onBlur, onFocus, ...restProps } = props;
    const checkboxProps = useCheckbox(ref, props);
    return /*#__PURE__*/ jsx(CheckboxBase, {
        ...mergeProps$1(checkboxProps, restProps),
        isOptional: isOptional,
        hasNecessityIndicator: hasNecessityIndicator,
        tooltip: tooltip,
        error: error,
        hasBorder: hasBorder,
        children: children
    });
});
/**
 * Internal implementation that use multiple checkbox hook (group)
 */ const CheckboxInGroup = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { children, hasBorder, tooltip, error } = props;
    const checkboxProps = useCheckboxInGroup(ref, props);
    return /*#__PURE__*/ jsx(CheckboxBase, {
        ...checkboxProps,
        tooltip: tooltip,
        error: error,
        hasBorder: hasBorder,
        isGroup: true,
        children: children
    });
});
/**
 * Internal implementation of Checkbox (style component)
 */ const CheckboxBase = (props)=>{
    const { children, boxProps, isDisabled, isOptional, hasNecessityIndicator, direction, isGroup, tooltip, error, errorId, hasBorder = false, isFocused, isHovered, isPressed, isSelected, ...restProps } = props;
    const cardBorderStyle = useMemo(()=>mapCardBorderProp({
            isHovered,
            isPressed,
            isSelected,
            hasBorder,
            isDisabled,
            error
        }), [
        isHovered,
        isPressed,
        isSelected,
        hasBorder,
        isDisabled,
        error
    ]);
    return /*#__PURE__*/ jsxs(Box, {
        ...restProps,
        display: "inline-flex",
        flexDirection: "column",
        children: [
            /*#__PURE__*/ jsxs(Box, {
                display: isGroup ? 'block' : 'inline-flex',
                height: "100%",
                children: [
                    /*#__PURE__*/ jsxs(Box, {
                        ...mergeProps$1(boxProps.checkbox, cardBorderStyle),
                        as: "label",
                        display: isGroup ? 'flex' : 'inline-flex',
                        alignItems: "flex-start",
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        typography: "unsafe_component.checkbox.typography.default",
                        color: `unsafe_component.checkbox.color.label${isDisabled ? 'Disabled' : ''}`,
                        paddingVertical: "unsafe_component.checkbox.spacing.padding.vertical",
                        height: "100%",
                        children: [
                            /*#__PURE__*/ jsx(VisuallyHidden, {
                                elementType: "span",
                                children: /*#__PURE__*/ jsx(Box, {
                                    ...boxProps.input,
                                    as: "input"
                                })
                            }),
                            /*#__PURE__*/ jsx(CheckboxTick, {
                                isFocused: isFocused,
                                isHovered: isHovered,
                                isPressed: isPressed,
                                isSelected: isSelected,
                                isDisabled: isDisabled,
                                error: error,
                                marginRight: "unsafe_component.checkbox.spacing.gap"
                            }),
                            /*#__PURE__*/ jsx(CheckboxLabel, {
                                isGroup: isGroup,
                                isOptional: isOptional,
                                hasNecessityIndicator: hasNecessityIndicator,
                                children: children
                            })
                        ]
                    }),
                    !isGroup && tooltip ? /*#__PURE__*/ jsx(Box, {
                        marginTop: "unsafe_component.checkbox.spacing.margin",
                        children: /*#__PURE__*/ jsx(Tooltip, {
                            content: tooltip,
                            children: /*#__PURE__*/ jsx(FieldTooltipTrigger, {})
                        })
                    }) : null
                ]
            }),
            error && !isGroup && !isDisabled ? /*#__PURE__*/ jsx(StateMessage, {
                id: errorId,
                variant: "error",
                marginTop: hasBorder ? 'unsafe_component.checkbox.spacing.gap' : undefined,
                children: error
            }) : null
        ]
    });
};
const CheckboxLabel = ({ children, isGroup, isOptional, hasNecessityIndicator })=>{
    return /*#__PURE__*/ jsxs(Box, {
        as: "span",
        children: [
            children,
            !isGroup && hasNecessityIndicator ? /*#__PURE__*/ jsx(FieldNecessityIndicator, {
                isOptional: isOptional,
                marginLeft: "unsafe_component.checkbox.spacing.gap",
                color: "unsafe_component.checkbox.color.optional",
                typography: "unsafe_component.checkbox.typography.optional"
            }) : null
        ]
    });
};
const CheckboxTick = ({ isHovered, isPressed, isFocused, isSelected, isDisabled, error, ...restProps })=>{
    const stateProps = useMemo(()=>mapCheckboxStateToBoxProps({
            isDisabled,
            isHovered,
            isPressed,
            isSelected,
            isInError: Boolean(error)
        }), [
        isDisabled,
        isHovered,
        isPressed,
        isSelected,
        error
    ]);
    return /*#__PURE__*/ jsx(Box, {
        ...restProps,
        as: "span",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "unsafe_component.checkbox.sizing.checkboxWrapper",
        height: "unsafe_component.checkbox.sizing.checkboxWrapper",
        flexShrink: 0,
        children: /*#__PURE__*/ jsx(Box, {
            as: "span",
            ...stateProps,
            width: "unsafe_component.checkbox.sizing.checkbox",
            height: "unsafe_component.checkbox.sizing.checkbox",
            borderStyle: "solid",
            borderRadius: "unsafe_component.checkbox.radius",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transitionDuration: "unsafe_component.checkbox.motionDuration",
            transitionProperty: "background-color",
            transitionTimingFunction: "unsafe_component.checkbox.motionEasing",
            flexShrink: 0,
            outlineWidth: "borderWidth.2",
            outlineStyle: isFocused ? 'auto' : 'none',
            outlineColor: [
                'Highlight',
                '-webkit-focus-ring-color'
            ],
            children: /*#__PURE__*/ jsx(CheckFilledIcon, {
                size: "inherit",
                transform: isSelected ? 'scale(1)' : 'scale(0)',
                transitionDuration: "unsafe_component.checkbox.motionDuration",
                transitionProperty: "transform",
                transitionTimingFunction: "unsafe_component.checkbox.motionEasing"
            })
        })
    });
};
const mapCheckboxStateToBoxProps = ({ isSelected, isDisabled, isHovered, isPressed, isInError })=>{
    const getBackgroundColor = ()=>{
        switch(true){
            case !isSelected:
                return `unsafe_component.checkbox.color.bg`;
            case isDisabled:
                return `unsafe_component.checkbox.color.bgSelectedDisabled`;
            case isInError && isPressed:
                return `unsafe_component.checkbox.color.bgSelectedErrorPressed`;
            case isInError && isHovered:
                return `unsafe_component.checkbox.color.bgSelectedErrorHover`;
            case isPressed:
                return `unsafe_component.checkbox.color.bgSelectedPressed`;
            case isHovered:
                return `unsafe_component.checkbox.color.bgSelectedHover`;
            case isInError:
                return `unsafe_component.checkbox.color.bgSelectedError`;
            default:
                return `unsafe_component.checkbox.color.bgSelected`;
        }
    };
    const getBorderColor = ()=>{
        switch(true){
            case isDisabled:
                return `unsafe_component.checkbox.color.borderDisabled`;
            case isInError && isPressed:
                return `unsafe_component.checkbox.color.borderErrorPressed`;
            case isInError && isHovered:
                return `unsafe_component.checkbox.color.borderErrorHover`;
            case isPressed:
                return `unsafe_component.checkbox.color.borderPressed`;
            case isHovered:
                return `unsafe_component.checkbox.color.borderHover`;
            case isInError:
                return `unsafe_component.checkbox.color.borderError`;
            case isSelected:
                return `unsafe_component.checkbox.color.borderSelected`;
            default:
                return `unsafe_component.checkbox.color.border`;
        }
    };
    return {
        backgroundColor: getBackgroundColor(),
        borderColor: getBorderColor(),
        borderWidth: isInError ? 'unsafe_component.checkbox.borderWidth.error' : 'unsafe_component.checkbox.borderWidth.default',
        color: `unsafe_component.checkbox.color.icon${isDisabled ? 'Disabled' : ''}`
    };
};
const mapCardBorderProp = ({ hasBorder, isSelected, isDisabled, isHovered, isPressed, error })=>{
    if (!hasBorder) {
        return undefined;
    }
    const getBorderColor = ()=>{
        const isInError = Boolean(error);
        switch(true){
            case isDisabled:
                return `unsafe_component.checkbox.color.borderDisabled`;
            case isInError && isPressed:
                return `unsafe_component.checkbox.color.borderErrorPressed`;
            case isInError && isHovered:
                return `unsafe_component.checkbox.color.borderErrorHover`;
            case isPressed:
                return `unsafe_component.checkbox.color.borderPressed`;
            case isHovered:
                return `unsafe_component.checkbox.color.borderHover`;
            case isInError:
                return `unsafe_component.checkbox.color.borderError`;
            case isSelected:
                return `unsafe_component.checkbox.color.borderSelected`;
            default:
                return `unsafe_component.checkbox.color.borderDisabled`;
        }
    };
    return {
        borderColor: getBorderColor(),
        borderRadius: 'unsafe_component.checkbox.radius',
        borderStyle: 'solid',
        borderWidth: 'unsafe_component.checkbox.borderWidth.default',
        paddingHorizontal: 'unsafe_component.checkbox.spacing.padding.horizontal'
    };
};

const useCheckboxGroup = (ref, { isDisabled, label, isOptional, error, ...rest })=>{
    const objectRef = useObjectRef(ref);
    const labelProp = typeof label !== 'string' && label.isHidden ? 'aria-label' : 'label';
    const propsComputed = {
        ...rest,
        [labelProp]: typeof label === 'string' ? label : label.content,
        isRequired: !isOptional,
        isDisabled: Boolean(isDisabled),
        errorMessage: error,
        isInvalid: Boolean(error)
    };
    const state = useCheckboxGroupState(propsComputed);
    const { groupProps, labelProps, descriptionProps, errorMessageProps } = useCheckboxGroup$1(propsComputed, state);
    return {
        boxProps: {
            group: mergeProps$1(groupProps, {
                ref: objectRef
            }),
            label: labelProps,
            error: errorMessageProps,
            helper: descriptionProps
        },
        state
    };
};

const CheckboxGroup = /*#__PURE__*/ forwardRef(function CheckboxGroup(props, ref) {
    const { direction = 'vertical', name, onChange, onBlur, onFocus, value, defaultValue, items, hasTwoColumns = false, hasBorder = false, ...fieldProps } = props;
    const { boxProps, state } = useCheckboxGroup(ref, props);
    const responsiveFlexDirection = useMemo(()=>mapFlexDirectionProp({
            direction
        }), [
        direction
    ]);
    const responsiveFlexWrap = useMemo(()=>mapFlexWrapProp({
            direction
        }), [
        direction
    ]);
    return /*#__PURE__*/ jsx(FieldBase, {
        position: "relative",
        labelProps: boxProps.label,
        helperProps: boxProps.helper,
        errorProps: boxProps.error,
        ...fieldProps,
        disableMargin: true,
        children: /*#__PURE__*/ jsx(CheckboxGroupContext.Provider, {
            value: {
                state,
                direction
            },
            children: /*#__PURE__*/ jsx(Box, {
                ...boxProps.group,
                ref: boxProps.group.ref,
                display: hasTwoColumns ? 'grid' : 'flex',
                flexDirection: !hasTwoColumns ? responsiveFlexDirection : 'column',
                flexWrap: !hasTwoColumns ? responsiveFlexWrap : undefined,
                gridTemplateColumns: hasTwoColumns ? 'repeat(2, 1fr)' : undefined,
                // Grid gap
                columnGap: direction !== 'vertical' || hasTwoColumns ? 'unsafe_component.checkboxGroup.spacing.gap' : undefined,
                rowGap: hasBorder ? 'unsafe_component.checkboxGroup.spacing.gap' : undefined,
                marginTop: hasBorder ? 'unsafe_component.checkboxGroup.spacing.margin' : undefined,
                marginBottom: hasBorder && fieldProps.error ? 'unsafe_component.checkboxGroup.spacing.margin' : undefined,
                children: props.items.map(({ label, value, isDisabled })=>/*#__PURE__*/ jsx(CheckboxInGroup, {
                        name: props.name,
                        value: value,
                        isDisabled: Boolean(fieldProps.isDisabled) || Boolean(isDisabled),
                        error: fieldProps.error,
                        hasBorder: hasBorder,
                        children: label
                    }, value))
            })
        })
    });
});
const mapFlexDirectionProp = ({ direction })=>{
    if (typeof direction === 'object') {
        return Object.entries(direction).reduce((acc, [breakpoint, direction])=>{
            return {
                ...acc,
                [breakpoint]: direction === 'vertical' ? 'column' : 'row'
            };
        }, {});
    }
    if (direction === 'vertical') {
        return 'column';
    }
    return 'row';
};
const mapFlexWrapProp = ({ direction })=>{
    if (typeof direction === 'object') {
        return Object.entries(direction).reduce((acc, [breakpoint, direction])=>{
            return {
                ...acc,
                [breakpoint]: direction === 'vertical' ? 'nowrap' : 'wrap'
            };
        }, {});
    }
    if (direction === 'vertical') {
        return 'nowrap';
    }
    return 'wrap';
};

// https://react-spectrum.adobe.com/internationalized/date/Calendar.html#calendar-identifiers
// As we manage only FR/DE/BE/NL, we only need Gregorian calendar for now
function createCalendar(identifier) {
    if (identifier === 'gregory') {
        return new GregorianCalendar();
    }
    throw new Error(`Unsupported calendar ${identifier}`);
}

const CHART_HEADER_DEFAULT_TITLE_AS = `h2`;
const CHART_HEADER_GROUP_MAX_WIDTH = 600;

const CHART_HEADER_I18N = {
    'en-GB': {
        'chartHeader::group::filters::label': 'Filters',
        'chartHeader::modal::title': 'Filter the data',
        'chartHeader::modal::trigger::label': 'Filters'
    },
    'fr-FR': {
        'chartHeader::group::filters::label': 'Filtres',
        'chartHeader::modal::title': 'Filtrer les données',
        'chartHeader::modal::trigger::label': 'Filtres'
    },
    'de-DE': {
        'chartHeader::group::filters::label': 'Filter',
        'chartHeader::modal::title': 'Daten filtern',
        'chartHeader::modal::trigger::label': 'Filter'
    },
    'nl-NL': {
        'chartHeader::group::filters::label': 'Filteren',
        'chartHeader::modal::title': 'Filter de gegevens',
        'chartHeader::modal::trigger::label': 'Filteren'
    }
};
const useChartHeaderTranslation = ()=>useTranslation(CHART_HEADER_I18N);

/**
 * The `ChartHeader` component is used to create a content section for a chart, with optional title and subtitle,
 * as well as any filter elements that can manipulate the data displayed in the chart.
 *
 * The filters can be displayed beside the header or in a modal, that is accessible through a button.
 */ const ChartHeader = ({ title, titleAs = CHART_HEADER_DEFAULT_TITLE_AS, subtitle, children, chartId, showFiltersInModal = false, modalProps: { children: modalChildren, ...modalProps } = {}, ...containerBoxProps })=>{
    const t = useChartHeaderTranslation();
    const hasHeadingGroup = !!title || !!subtitle;
    const hasFilters = !!children;
    return /*#__PURE__*/ jsx(Box, {
        ...containerBoxProps,
        as: "header",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        columnGap: "spacing.4",
        children: /*#__PURE__*/ jsxs(Box, {
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            gap: "spacing.8",
            flexShrink: "1",
            flexGrow: "1",
            justifyContent: hasHeadingGroup && !hasFilters ? 'flex-start' : !hasHeadingGroup && hasFilters ? 'flex-end' : 'space-between',
            children: [
                hasHeadingGroup && /*#__PURE__*/ jsx(ChartHeaderHeadingGroup, {
                    title: title,
                    subtitle: subtitle,
                    titleAs: titleAs
                }),
                hasFilters && showFiltersInModal ? /*#__PURE__*/ jsx(Modal, {
                    title: t('chartHeader::modal::title'),
                    isDismissable: true,
                    ...modalProps,
                    trigger: (triggerProps)=>/*#__PURE__*/ jsx(Button, {
                            variant: "secondary",
                            icon: FilterSlidersIcon,
                            ...triggerProps,
                            children: t('chartHeader::modal::trigger::label')
                        }),
                    children: (close)=>/*#__PURE__*/ jsxs(Fragment, {
                            children: [
                                modalChildren,
                                /*#__PURE__*/ jsx(ChartHeaderFilters, {
                                    renderProps: {
                                        'aria-controls': chartId
                                    },
                                    context: {
                                        closeModal: close
                                    },
                                    children: children
                                })
                            ]
                        })
                }) : hasFilters && /*#__PURE__*/ jsx(ChartHeaderFilters, {
                    renderProps: {
                        'aria-controls': chartId
                    },
                    children: children
                })
            ]
        })
    });
};
const ChartHeaderHeadingGroup = ({ title, titleAs = CHART_HEADER_DEFAULT_TITLE_AS, subtitle })=>{
    const titleId = useId$1();
    const hasSemanticHeading = titleAs.startsWith('h');
    const isGroup = !!title && !!subtitle;
    return(// Adding "group" role explicitly to avoid testing issues due to `testing-library` not recognizing "hgroup"
    // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/hgroup#technical_summary
    // https://github.com/testing-library/dom-testing-library/blob/main/package.json#L65
    // https://github.com/A11yance/aria-query/blob/v5.3.0/src/etc/roles/literal/groupRole.js#L4
    // https://github.com/A11yance/aria-query/blob/main/src/etc/roles/literal/groupRole.js#L38
    /*#__PURE__*/ jsxs(Box, {
        as: hasSemanticHeading && isGroup ? 'hgroup' : 'div',
        role: isGroup ? 'group' : undefined,
        "aria-labelledby": isGroup ? titleId : undefined,
        display: "flex",
        flexDirection: "column",
        rowGap: "spacing.8",
        flexGrow: "1",
        maxWidth: CHART_HEADER_GROUP_MAX_WIDTH,
        children: [
            title ? /*#__PURE__*/ jsx(Box, {
                as: isAllowedTitleTag(titleAs) ? titleAs : CHART_HEADER_DEFAULT_TITLE_AS,
                id: isGroup ? titleId : undefined,
                role: !hasSemanticHeading ? 'heading' : undefined,
                "aria-level": !hasSemanticHeading ? ALLOWED_TITLE_TAGS_TO_ARIA_LEVELS[titleAs] : undefined,
                marginTop: "0",
                marginBottom: "0",
                typography: "typography.headline.20.bold",
                color: "color.content.default.default",
                children: title
            }) : null,
            subtitle ? /*#__PURE__*/ jsx(Box, {
                as: "p",
                marginTop: "0",
                marginBottom: "0",
                typography: "typography.body.14.regular",
                color: "color.content.subdued.default",
                children: subtitle
            }) : null
        ]
    }));
};
const ChartHeaderFilters = ({ children, renderProps, context })=>{
    const t = useChartHeaderTranslation();
    const childrenAsArray = Array.isArray(children) ? children : [
        children
    ];
    return /*#__PURE__*/ jsx(Box, {
        role: "group",
        "aria-label": t('chartHeader::group::filters::label'),
        ...childrenAsArray.length > 1 ? {
            display: 'flex',
            gap: 'spacing.16',
            flexWrap: 'wrap',
            alignItems: 'center'
        } : {},
        children: childrenAsArray.map((child, index)=>/*#__PURE__*/ jsx(Box, {
                children: child(renderProps, context)
            }, index))
    });
};

/**
 * Due to `recharts` limitation, `CartesianGrid` can only be direclty returned from a render function to be reused.
 */ const withChartGrid = ({ isVisible = true, showVerticalLines = true, showHorizontalLines = true })=>()=>{
        const tokens = useTokens();
        if (!isVisible) {
            return null;
        }
        return /*#__PURE__*/ jsx(CartesianGrid, {
            syncWithTicks: true,
            horizontal: showHorizontalLines,
            vertical: showVerticalLines,
            stroke: tokens[`color.border.light.default`]
        });
    };
/**
 * Hook to provide render functions for `ChartGrid` based on the provided props.
 *
 * The `render` functions can only be called within a chart component from `recharts` like `LineChart`, `BarChart` etc.
 *
 * If `isVisible` is passed as value for `props`, the render functions will return `null` instead of `JSX.Element`.
 * This is to accomodate the top-level calling limitation of react hooks and to provide a consistent render cycle.
 *
 * @throws {Error} Throws if the `render` functions are called outside of an active `recharts` context.
 */ const useChartGrid = (props)=>{
    const render = withChartGrid(props);
    return {
        render
    };
};

/**
 * Removes properties with a specific value from an object of props and narrows the type.
 *
 * @param props The props object to filter.
 * @param filteredValue The value to be removed from the props object.
 * @returns A new object with the specified value removed.
 */ const filterProps = (props, filteredValue)=>{
    return Object.fromEntries(Object.entries(props).filter(([, value])=>value !== filteredValue));
};

/**
 * Hook to provide render functions for `ChartAxis` X & Y based on the provided props.
 *
 * The `render` functions can only be called within a chart component from `recharts` like `LineChart`, `BarChart` etc.
 *
 * If `isVisible` is passed as value for `props`, the render functions will return `null` instead of `JSX.Element`.
 * This is to accomodate the top-level calling limitation of react hooks and to provide a consistent render cycle.
 *
 * @throws {Error} Throws if the `render` functions are called outside of an active `recharts` context.
 */ const useChartAxis = ({ isVisible = true, ...props })=>{
    const axisYId = useId$1();
    const axisXId = useId$1();
    const { mapping = {
        y: 'number',
        x: 'category'
    }, titles, dataKey, domain, ticks, tickCount, allowDecimals, formatterCategory, formatterValue, chartType, testId } = props;
    const categoryProps = {
        type: 'category',
        dataKey,
        ...formatterCategory ? {
            tickFormatter: formatterCategory
        } : {},
        ...ticks?.category ? {
            ticks: ticks.category
        } : {}
    };
    const numberProps = {
        type: 'number',
        ...domain ? {
            domain
        } : {},
        ...formatterValue ? {
            tickFormatter: formatterValue
        } : {},
        ...ticks?.number ? {
            ticks: ticks.number
        } : {},
        ...tickCount !== undefined ? {
            tickCount
        } : {},
        ...allowDecimals !== undefined ? {
            allowDecimals
        } : {}
    };
    let titleYProps = {
        isVisible: false
    };
    if (isVisible && titles?.y) {
        titleYProps = {
            id: axisYId,
            title: titles.y,
            variant: 'y',
            isVisible: true,
            testId
        };
    }
    let titleXProps = {
        isVisible: false
    };
    if (isVisible && titles?.x) {
        titleXProps = {
            id: axisXId,
            title: titles.x,
            variant: 'x',
            isVisible: true,
            testId
        };
    }
    let axisYProps = {
        isVisible: false
    };
    let axisXProps = {
        isVisible: false
    };
    if (isVisible) {
        axisYProps = {
            ...mapping.y === 'number' ? numberProps : categoryProps,
            'aria-describedby': titles?.y ? axisYId : undefined,
            isVisible: true,
            chartType
        };
        axisXProps = {
            ...mapping.x === 'number' ? numberProps : categoryProps,
            'aria-describedby': titles?.x ? axisXId : undefined,
            isVisible: true,
            chartType
        };
    }
    return {
        render: {
            titleY: withChartTitle(titleYProps),
            axisY: withChartYAxis(axisYProps),
            titleX: withChartTitle(titleXProps),
            axisX: withChartXAxis(axisXProps)
        }
    };
};
/**
 * Due to `recharts` limitation, `YAxis` can only be directly returned from a render function to be reused.
 */ const withChartYAxis = ({ dataKey, tickFormatter, ticks, tickCount, allowDecimals, domain, type, 'aria-describedby': ariaDescribedBy, isVisible })=>({ width } = {})=>{
        const tokens = useTokens();
        const props = filterProps({
            dataKey,
            tickFormatter,
            domain,
            ticks,
            tickCount,
            allowDecimals,
            type,
            'aria-describedby': ariaDescribedBy,
            width
        }, undefined);
        if (!isVisible) {
            return null;
        }
        return /*#__PURE__*/ jsx(YAxis, {
            ...props,
            stroke: tokens['color.content.light.default'],
            tick: {
                fill: tokens['color.content.light.default']
            },
            tickLine: false,
            axisLine: type === 'category',
            style: {
                font: tokens['typography.body.12.regular'].font
            }
        });
    };
/**
 * Due to `recharts` limitation, `XAxis` can only be directly returned from a render function to be reused.
 */ const withChartXAxis = ({ dataKey, tickFormatter, domain, ticks, tickCount, allowDecimals, type, 'aria-describedby': ariaDescribedBy, isVisible, chartType })=>({ height } = {})=>{
        const tokens = useTokens();
        const props = filterProps({
            dataKey,
            tickFormatter,
            domain,
            ticks,
            tickCount,
            allowDecimals,
            type,
            'aria-describedby': ariaDescribedBy,
            height
        }, undefined);
        const axisHorizontalPadding = parseFloat(tokens['spacing.40'].toString()) * getRootFontSizeValue();
        if (!isVisible) {
            return null;
        }
        return /*#__PURE__*/ jsx(XAxis, {
            ...props,
            stroke: tokens['color.content.light.default'],
            tick: {
                fill: tokens['color.content.light.default']
            },
            axisLine: type === 'category',
            tickLine: false,
            padding: chartType === 'line' ? {
                left: axisHorizontalPadding,
                right: axisHorizontalPadding
            } : undefined,
            style: {
                font: tokens['typography.body.12.regular'].font
            }
        });
    };
const withChartTitle = ({ id, title, variant, isVisible, testId })=>()=>{
        if (!isVisible) {
            return null;
        }
        return /*#__PURE__*/ jsx(ChartAxisTitle, {
            id: id,
            title: title,
            variant: variant,
            testId: testId
        });
    };
const ChartAxisTitle = ({ id, title, variant, testId })=>/*#__PURE__*/ jsx(Box, {
        as: "span",
        id: id,
        display: "block",
        marginBottom: variant === 'y' ? 'spacing.8' : undefined,
        marginTop: variant === 'x' ? 'spacing.4' : undefined,
        textAlign: variant === 'y' ? 'left' : 'center',
        color: "color.content.light.default",
        typography: "typography.body.12.regular",
        testId: testId ? `${testId}-title-${variant}` : undefined,
        children: title
    });

const LegendLabel = ({ entry, formatter, index })=>{
    const finalValue = formatter ? formatter(entry.label, entry, index) : entry.label;
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx(Box, {
                as: "span",
                "aria-hidden": "true",
                display: "inline-block",
                width: "12px",
                height: "12px",
                marginVertical: "auto",
                marginRight: "spacing.4",
                borderRadius: "radius.rounded",
                backgroundColor: `color.surface.data.${entry.color}`,
                verticalAlign: "middle"
            }),
            /*#__PURE__*/ jsx(Box, {
                as: "span",
                typography: "typography.body.14.regular",
                children: finalValue
            })
        ]
    });
};

const useLegendTranslation = ()=>useTranslation({
        'en-GB': {
            'chartlegend::noninteractive::label': 'Legend',
            'chartlegend::interactive::label': 'Toggle a checkbox to change the visibility of data in the chart'
        },
        'fr-FR': {
            'chartlegend::noninteractive::label': 'Légende',
            'chartlegend::interactive::label': 'Cochez une case pour modifier la visibilité des données dans le graphique'
        },
        'de-DE': {
            'chartlegend::noninteractive::label': 'Legende',
            'chartlegend::interactive::label': 'Aktiviere oder deaktiviere eine Checkbox, um die Sichtbarkeit der Daten im Diagramm zu ändern'
        },
        'nl-NL': {
            'chartlegend::noninteractive::label': 'Legenda',
            'chartlegend::interactive::label': 'Schakel een selectievakje in om de zichtbaarheid van gegevens in de grafiek te wijzigen'
        }
    });

const InteractiveLegend = ({ containerRef, chartId, items, value, onChange, ...containerBoxProps })=>{
    const t = useLegendTranslation();
    return /*#__PURE__*/ jsx(CheckboxGroup, {
        ...containerBoxProps,
        ref: containerRef,
        items: items,
        isOptional: true,
        label: {
            isHidden: true,
            content: t('chartlegend::interactive::label')
        },
        name: "legend",
        direction: "horizontal",
        value: value,
        onChange: onChange,
        "aria-controls": chartId
    });
};

const NonInteractiveLegend = ({ containerRef, items, ...containerBoxProps })=>{
    const t = useLegendTranslation();
    const id = useId$1();
    return /*#__PURE__*/ jsx(Box, {
        ...containerBoxProps,
        children: /*#__PURE__*/ jsx(Box, {
            as: "ul",
            ref: containerRef,
            id: id,
            "aria-label": t('chartlegend::noninteractive::label'),
            display: "flex",
            flexWrap: "wrap",
            gap: "spacing.16",
            width: "100%",
            height: "sizing.48",
            margin: "0",
            padding: "0",
            children: items.map((item, index)=>/*#__PURE__*/ jsx(Box, {
                    as: "li",
                    display: "flex",
                    alignItems: "center",
                    children: item.label
                }, `legend-item-${index}`))
        })
    });
};

/**
 * The `ChartLegend` component is used to display a list of point of interest from a chart's data.
 *
 * It also offers a way to toggle the display of each point of interest in a chart.
 */ const ChartLegend = ({ containerRef, chartId, data, formatter, onChange, value, ...containerBoxProps })=>{
    const items = data.map((entry, index)=>({
            label: /*#__PURE__*/ jsx(LegendLabel, {
                index: index,
                entry: entry,
                formatter: formatter
            }),
            value: entry.key,
            isDisabled: value?.length === 1 && value.includes(entry.key)
        }));
    if (value && onChange) return /*#__PURE__*/ jsx(InteractiveLegend, {
        ...containerBoxProps,
        containerRef: containerRef,
        chartId: chartId,
        // todo: discover a way to allow custom label for CheckboxGroup
        items: items,
        value: value,
        onChange: onChange
    });
    return /*#__PURE__*/ jsx(NonInteractiveLegend, {
        ...containerBoxProps,
        containerRef: containerRef,
        // todo: discover a way to allow custom label for CheckboxGroup
        items: items
    });
};

/**
 * The `ChartTooltip` component is used to display accurate information about specific points of interest in a chart.
 */ const ChartTooltip = ({ label, payload, formatter })=>{
    const labelId = useId$1();
    return /*#__PURE__*/ jsxs(Box, {
        role: "status",
        "aria-live": "assertive",
        display: "flex",
        flexDirection: "column",
        borderRadius: "radius.4",
        paddingHorizontal: "spacing.12",
        paddingVertical: "spacing.8",
        backgroundColor: "color.surface.default.inverted.default",
        color: "color.content.default.inverted.default",
        "aria-labelledby": labelId,
        children: [
            /*#__PURE__*/ jsx(Box, {
                as: "strong",
                id: labelId,
                margin: "0",
                typography: "typography.body.12.regular",
                children: label
            }),
            /*#__PURE__*/ jsx(Box, {
                as: "dl",
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: "spacing.4",
                marginTop: "spacing.8",
                marginBottom: "0",
                padding: "0",
                typography: "typography.body.12.bold",
                children: payload.map((entry, index)=>{
                    const [name, value] = normalizedFormatter(formatter, entry, index, payload);
                    return /*#__PURE__*/ jsxs(Fragment$1, {
                        children: [
                            /*#__PURE__*/ jsxs(Box, {
                                as: "dt",
                                marginVertical: "auto",
                                children: [
                                    /*#__PURE__*/ jsx(Box, {
                                        as: "span",
                                        display: "block",
                                        width: "6px",
                                        height: "6px",
                                        borderRadius: "radius.rounded",
                                        borderStyle: "solid",
                                        borderWidth: "borderWidth.1",
                                        borderColor: "color.border.surface.default",
                                        unsafe_style: {
                                            backgroundColor: entry.color
                                        },
                                        "aria-hidden": "true"
                                    }),
                                    /*#__PURE__*/ jsx(VisuallyHidden, {
                                        children: `${name}:`
                                    })
                                ]
                            }),
                            /*#__PURE__*/ jsx(Box, {
                                as: "dd",
                                marginLeft: 0,
                                children: /*#__PURE__*/ jsx(Box, {
                                    as: "data",
                                    value: entry.value,
                                    children: value
                                })
                            })
                        ]
                    }, entry.dataKey);
                })
            })
        ]
    });
};
const normalizedFormatter = (formatter, entry, index, payload)=>{
    if (!formatter) {
        return [
            entry.name,
            entry.value
        ];
    }
    const result = formatter(entry.value, entry.name, entry, index, payload);
    // Both name and value have been formatted when array
    if (Array.isArray(result)) {
        return [
            result[1],
            result[0]
        ];
    }
    // Only value has been formatted when no array
    return [
        entry.name,
        result
    ];
};

const useChartTooltip = (props)=>{
    return {
        render: withChartTooltip(props)
    };
};
/**
 * Due to `recharts` limitation, `Tooltip` can only be directly returned from a render function to be reused.
 */ const withChartTooltip = ({ formatter, chartType })=>()=>{
        const tokens = useTokens();
        const props = filterProps({
            formatter
        }, undefined);
        const cursorStyle = chartType === 'line' ? {
            strokeWidth: 0.5,
            stroke: tokens['color.border.default.default']
        } : chartType === 'bar' ? {
            fill: tokens['color.surface.onSecondary.transparent.hover']
        } : {};
        return /*#__PURE__*/ jsx(Tooltip$1, {
            ...props,
            content: renderTooltip,
            cursor: cursorStyle
        });
    };
const renderTooltip = ({ active, label, formatter, payload })=>{
    if (!active || !label || !payload || payload.length === 0) {
        return null;
    }
    const filteredPayload = payload.filter((entry)=>isNonEmptyString(entry.name) && isValidNumber(entry.value) && isNonEmptyString(entry.dataKey) && isNonEmptyString(entry.color));
    return /*#__PURE__*/ jsx(ChartTooltip, {
        label: label,
        payload: filteredPayload,
        formatter: formatter
    });
};
const isNonEmptyString = (value)=>typeof value === 'string' && value.trim() !== '';
const isValidNumber = (value)=>typeof value === 'number' && !Number.isNaN(value);

const Y_PADDING = 8;
const X_PADDING = 12;
const Y_TICK_VALUE_SELECTOR = '.recharts-yAxis .recharts-cartesian-axis-tick';
const X_TICK_VALUE_SELECTOR = '.recharts-xAxis .recharts-cartesian-axis-tick';
/**
 * This hook is used to dynamically set the width of the y-axis and the height of the x-axis based on the size of the tick values.
 * This is used to ensure that the y-axis and x-axis labels (and tick) are not cut off.
 */ const useDynamicAxisSize = ({ data, direction })=>{
    const [axisSize, setAxisSize] = useState(undefined);
    const setChartRef = useCallback((chartRef)=>{
        if (chartRef && chartRef.container) {
            const yTickValueElements = chartRef.container.querySelectorAll(Y_TICK_VALUE_SELECTOR);
            const xTickValueElements = chartRef.container.querySelectorAll(X_TICK_VALUE_SELECTOR);
            const highestWidth = Array.from(yTickValueElements).reduce((maxWidth, el)=>{
                const width = el.getBoundingClientRect().width;
                return Math.max(maxWidth, width);
            }, 0);
            const highestHeight = Array.from(xTickValueElements).reduce((maxHeight, el)=>{
                const height = el.getBoundingClientRect().height;
                return Math.max(maxHeight, height);
            }, 0);
            setAxisSize({
                xAxisHeight: highestHeight + X_PADDING,
                yAxisWidth: highestWidth + Y_PADDING
            });
        }
    }, // If direction is set dynamically consumer side, re-computes correct axis sizes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        data,
        direction
    ]);
    return {
        axisSize,
        setChartRef
    };
};

export { ALLOWED_TITLE_TAGS as A, Button as B, CheckboxTick as C, DEFAULT_LABEL_AS as D, ALLOWED_TITLE_TAGS_TO_ARIA_LEVELS as E, FieldBase as F, filterProps as G, Modal as M, StateMessage as S, Tooltip as T, useField as a, useButton as b, Badge as c, createCalendar as d, Backdrop as e, FieldLabel as f, useCheckboxGroup as g, CheckboxGroupContext as h, isAllowedTitleTag as i, useCheckboxInGroup as j, useEnterAnimation as k, useCheckbox as l, FieldNecessityIndicator as m, FieldTooltipTrigger as n, ButtonBar as o, Checkbox as p, CheckboxGroup as q, useTooltipTrigger as r, TopBar as s, ChartHeader as t, useExitAnimation as u, useChartGrid as v, useChartAxis as w, ChartLegend as x, useChartTooltip as y, useDynamicAxisSize as z };
//# sourceMappingURL=unsafe-Br2TKd7D.mjs.map
